import ProductLocationsApiV2 from 'api/v2/admins/product-locations-api-v2'
import StockOpnameEntriesApiV2 from 'api/v2/admins/stock-opname-entries-api-v2'
import RedirectButton from 'components/atoms/redirect-button'
import TitlePage from 'components/atoms/title-page'
import SearchableSelect from 'components/atoms/searchable-select'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { Alert, Badge, Button, Card, CardBody, Col, Form, FormGroup, Input, Label, Row, Spinner, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const CORRECTION_STATUS_OPTIONS = [
  { key: '', label: 'No Correction' },
  { key: 'needs_outbound_check', label: 'Needs Outbound Check' },
  { key: 'needs_seller_return', label: 'Needs Seller Return' },
  { key: 'resolved', label: 'Resolved' }
]

const emptyFormValues = {
  product_found                  : false,
  physical_check_status          : '',
  price_tag_exist                : false,
  all_inclusions_found           : false,
  inclusions_in_product          : '',
  inclusions_in_box              : '',
  inclusions_in_safe_deposit_box : '',
  status                         : '',
  status_date                    : '',
  actual_quantity                : '',
  correction_status              : '',
  correction_note                : '',
  internal_note                  : '',
  item_location_id               : '',
  box_location_id                : '',
  inclusions_location_id         : ''
}

const hydrateFormValues = (entry) => ({
  product_found                  : !!entry?.product_found,
  physical_check_status          : entry?.physical_check_status === 'pending' ? '' : (entry?.physical_check_status || ''),
  price_tag_exist                : !!entry?.price_tag_exist,
  all_inclusions_found           : !!entry?.all_inclusions_found,
  inclusions_in_product          : entry?.inclusions_in_product || '',
  inclusions_in_box              : entry?.inclusions_in_box || '',
  inclusions_in_safe_deposit_box : entry?.inclusions_in_safe_deposit_box || '',
  status                         : entry?.status || '',
  status_date                    : entry?.status_date_input || '',
  actual_quantity                : entry?.actual_quantity ?? '',
  correction_status              : entry?.correction_status || '',
  correction_note                : entry?.correction_note || '',
  internal_note                  : entry?.product?.internal_note || '',
  item_location_id               : entry?.product?.item_location_id || '',
  box_location_id                : entry?.product?.box_location_id || '',
  inclusions_location_id         : entry?.product?.inclusions_location_id || ''
})

const internalCategoryLabel = (value) => {
  if (value === 'H') return 'H — Hermes'
  if (value === 'W') return 'W — Watch'
  if (value === 'C') return 'C — Curated'

  return '-'
}

const StockOpnameEntryEditPage = ({ pageUtils }) => {
  const { id } = useParams()
  const location = useLocation()
  const [ stockOpnameEntry, setStockOpnameEntry ] = useState(null)
  const [ formValues, setFormValues ] = useState(emptyFormValues)
  const [ itemLocations, setItemLocations ] = useState([])
  const [ boxLocations, setBoxLocations ] = useState([])
  const [ inclusionsLocations, setInclusionsLocations ] = useState([])
  const [ isLoading, setIsLoading ] = useState(true)
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ loadError, setLoadError ] = useState('')

  useEffect(() => {
    setIsLoading(true)
    setLoadError('')

    Promise.all([
      StockOpnameEntriesApiV2.show(id),
      ProductLocationsApiV2.get({ type_name: 'Lokasi Item' }),
      ProductLocationsApiV2.get({ type_name: 'Lokasi Box' }),
      ProductLocationsApiV2.get({ type_name: 'Lokasi Kelengkapan' })
    ])
      .then(([ entryResponse, itemResponse, boxResponse, inclusionsResponse ]) => {
        const entry = entryResponse?.data?.stock_opname_entry || null
        setStockOpnameEntry(entry)
        setFormValues(hydrateFormValues(entry))
        setItemLocations(itemResponse?.data?.product_locations || [])
        setBoxLocations(boxResponse?.data?.product_locations || [])
        setInclusionsLocations(inclusionsResponse?.data?.product_locations || [])
        setIsLoading(false)
      })
      .catch((error) => {
        setIsLoading(false)
        setLoadError('Unable to fetch stock opname entry detail')
        pageUtils?.setApiErrorMsg?.(error?.response?.data)
        pageUtils?.setAlertMsg?.('Unable to fetch stock opname entry detail', ALERT_TYPES.DANGER)
      })
  }, [ id, pageUtils ])

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePhysicalCheckStatusChange = (status) => {
    setFormValues((prev) => ({
      ...prev,
      physical_check_status : status,
      product_found         : status === 'found',
      actual_quantity       : status === 'found' ? 1 : 0
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setIsSubmitting(true)

    await StockOpnameEntriesApiV2.update({
      id,
      values: {
        product_found                  : formValues.product_found,
        physical_check_status          : formValues.physical_check_status,
        price_tag_exist                : formValues.price_tag_exist,
        all_inclusions_found           : formValues.all_inclusions_found,
        inclusions_in_product          : formValues.inclusions_in_product,
        inclusions_in_box              : formValues.inclusions_in_box,
        inclusions_in_safe_deposit_box : formValues.inclusions_in_safe_deposit_box,
        status                         : formValues.status,
        status_date                    : formValues.status_date || null,
        actual_quantity                : formValues.actual_quantity === '' ? null : formValues.actual_quantity,
        correction_status              : formValues.correction_status,
        correction_note                : formValues.correction_note,
        updated_at_token               : stockOpnameEntry?.updated_at_token,
        product_attributes             : {
          internal_note          : formValues.internal_note,
          item_location_id       : formValues.item_location_id || null,
          box_location_id        : formValues.box_location_id || null,
          inclusions_location_id : formValues.inclusions_location_id || null
        }
      }
    })
      .then((response) => {
        const entry = response?.data?.stock_opname_entry || null
        setStockOpnameEntry(entry)
        setFormValues(hydrateFormValues(entry))
        pageUtils?.setAlertMsg?.('Stock Opname Entry has been updated.', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error?.response?.data)
        pageUtils?.setAlertMsg?.(error?.response?.data?.messages?.base || 'Unable to update stock opname entry', ALERT_TYPES.DANGER)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const requestedReturnTo = new URLSearchParams(location.search).get('return_to')
  const fallbackBackPath = stockOpnameEntry?.stock_opname_id
    ? `/app/stock-opnames/${stockOpnameEntry.stock_opname_id}`
    : '/app/stock-opnames/list-page'
  const validReturnTo =
    requestedReturnTo?.startsWith('/app/stock-opname-entries/list-page') ||
    requestedReturnTo?.startsWith('/app/stock-opnames/')
  const backPath = validReturnTo ? requestedReturnTo : fallbackBackPath

  return (
    <>
      <TitlePage mainTitle={ 'Stock Opname Entry' } subTitle={ 'Edit' } />

      <div className='d-flex justify-content-between align-items-center mb-3'>
        <RedirectButton
          color='secondary'
          size='sm'
          text='Back'
          path={ backPath }
        />
        <Button
          color='success'
          disabled={ isSubmitting || isLoading || !stockOpnameEntry }
          type='submit'
          form='stock-opname-entry-form'
        >
          {
            isSubmitting
              ? <i className='ni ni-settings-gear-65 spin'/>
              : 'Save Stock Opname Entry'
          }
        </Button>
      </div>

      {
        isLoading
          ? (
            <Card body className='text-center'>
              <Spinner color='primary' />
            </Card>
          )
          : (
            <>
              {
                loadError && (
                  <Alert color='warning'>
                    { loadError }
                  </Alert>
                )
              }

              <Form id='stock-opname-entry-form' onSubmit={ handleSubmit }>
                <Card className='mb-3'>
                  <CardBody>
                    <Row className='mb-3'>
                      <Col xs={ 6 } md={ 3 }>
                        <div className='text-muted'>{ 'Sellable Quantity (SQ)' }</div>
                        <div style={{ fontSize: '24px', fontWeight: 700 }}>{ stockOpnameEntry?.current_sellable_quantity ?? '-' }</div>
                      </Col>
                      <Col xs={ 6 } md={ 3 }>
                        <div className='text-muted'>{ 'Inventory Quantity (IQ)' }</div>
                        <div style={{ fontSize: '24px', fontWeight: 700 }}>{ stockOpnameEntry?.current_inventory_quantity ?? '-' }</div>
                      </Col>
                      <Col md={ 6 }>
                        <div className='text-muted'>{ 'Current Inventory Location' }</div>
                        <div style={{ wordBreak: 'break-word' }}>{ stockOpnameEntry?.current_inventory_location_display || '-' }</div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={ 3 }>
                        <FormGroup check className='mb-3'>
                          <Label check className='text-success'>
                            <Input
                              type='radio'
                              name='physical_check_status'
                              checked={ formValues.physical_check_status === 'found' }
                              onChange={ () => handlePhysicalCheckStatusChange('found') }
                            />
                            { ' ✓ Cek Fisik (barang Ada)' }
                          </Label>
                        </FormGroup>
                      </Col>
                      <Col md={ 3 }>
                        <FormGroup check className='mb-3'>
                          <Label check className='text-danger'>
                            <Input
                              type='radio'
                              name='physical_check_status'
                              checked={ formValues.physical_check_status === 'not_found' }
                              onChange={ () => handlePhysicalCheckStatusChange('not_found') }
                            />
                            { ' ✕ Cek Fisik (barang Tidak Ada)' }
                          </Label>
                        </FormGroup>
                      </Col>
                      <Col md={ 3 }>
                        <FormGroup check className='mb-3'>
                          <Label check>
                            <Input
                              type='checkbox'
                              checked={ formValues.price_tag_exist }
                              onChange={ (event) => handleChange('price_tag_exist', event.target.checked) }
                            />
                            { ' Price Tag' }
                          </Label>
                        </FormGroup>
                      </Col>
                      <Col md={ 3 }>
                        <FormGroup check className='mb-3'>
                          <Label check>
                            <Input
                              type='checkbox'
                              checked={ formValues.all_inclusions_found }
                              onChange={ (event) => handleChange('all_inclusions_found', event.target.checked) }
                            />
                            { ' Cek Kelengkapan (lengkap)' }
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <div className='alert alert-info'>Koreksi Ada / Tidak Ada dapat diulang selama mutasi terakhir adalah cek fisik pada sesi STO dan hari yang sama. Barang Ada memulihkan IQ/SQ sebelum cek Tidak Ada, termasuk SQ 0 untuk barang yang belum sellable. Jika ada mutasi lain, hubungi tim terkait sebelum koreksi.</div>

                <Card className='mb-3'>
                  <CardBody>
                    <h4>{ 'Product Data' }</h4>
                    <ul className='mb-0 pl-4'>
                      <li><strong>{ 'Stock Code: ' }</strong>{ stockOpnameEntry?.stock_code || '-' }</li>
                      <li><strong>{ 'Specification: ' }</strong>{ stockOpnameEntry?.product_name || '-' }</li>
                      <li><strong>{ 'Internal Category: ' }</strong>{ internalCategoryLabel(stockOpnameEntry?.product?.internal_category) }</li>
                      <li>{ `Internal Search Keyword: ${stockOpnameEntry?.product?.internal_search_keyword || '-'}` }</li>
                      <li>{ `Date of Shipping: ${stockOpnameEntry?.product?.shipping_date || '-'}` }</li>
                      <li>{ `Stamp: ${stockOpnameEntry?.product?.stamp || '-'}` }</li>
                      <li>{ `Consignor/Seller: ${stockOpnameEntry?.product?.supplier_name || stockOpnameEntry?.supplier || '-'}` }</li>
                      <li>{ `Lokasi: ${stockOpnameEntry?.product?.item_location_label || '-'}` }</li>
                      <li>{ `Data Rak: ${stockOpnameEntry?.product?.box_location_label || '-'}` }</li>
                      <li><strong>{ 'Inventory ID: ' }</strong>{ stockOpnameEntry?.inventory_id || '-' }</li>
                      <li><strong>{ 'Current Inventory Location: ' }</strong>{ stockOpnameEntry?.current_inventory_location_display || '-' }</li>
                      <li>{ `Internal Note Produk: ${stockOpnameEntry?.product?.internal_note || '-'}` }</li>
                      <li><strong>{ 'Kelengkapan: ' }</strong>{ stockOpnameEntry?.product?.comes_with || '-' }</li>
                    </ul>
                  </CardBody>
                </Card>

                <Card className='mb-3'>
                  <CardBody>
                    <Row>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Actual Quantity di Lapangan' }</Label>
                          <Input
                            type='number'
                            min='0'
                            step='1'
                            value={ formValues.actual_quantity }
                            readOnly
                          />
                          <small className='text-muted'>
                            { `Auto dari Cek Fisik: Ada = 1, Tidak Ada = 0. Expected IQ snapshot: ${stockOpnameEntry?.expected_inventory_quantity || 0}` }
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Correction Status' }</Label>
                          <Input
                            type='select'
                            value={ formValues.correction_status }
                            onChange={ (event) => handleChange('correction_status', event.target.value) }
                          >
                            {
                              CORRECTION_STATUS_OPTIONS.map((option) => (
                                <option key={ option.key || 'none' } value={ option.key }>{ option.label }</option>
                              ))
                            }
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Correction Note' }</Label>
                          <Input
                            type='text'
                            value={ formValues.correction_note }
                            onChange={ (event) => handleChange('correction_note', event.target.value) }
                            placeholder='Contoh: Barang sudah return, buat Seller Return manual'
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Lokasi Produk' }</Label>
                          <SearchableSelect
                            options={ itemLocations.map((location) => ({ value: location.id, label: location.name })) }
                            value={ formValues.item_location_id }
                            onChange={ (value) => handleChange('item_location_id', value) }
                            placeholder='Search Lokasi Produk'
                          />
                          <small className='text-muted'>
                            { `Current mirror: ${stockOpnameEntry?.product?.item_location_label || '-'}` }
                          </small>
                        </FormGroup>
                      </Col>

                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Data Rak (Lokasi Box)' }</Label>
                          <SearchableSelect
                            options={ boxLocations.map((location) => ({ value: location.id, label: location.name })) }
                            value={ formValues.box_location_id }
                            onChange={ (value) => handleChange('box_location_id', value) }
                            placeholder='Search Data Rak (Lokasi Box)'
                          />
                        </FormGroup>
                      </Col>

                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Lokasi Kelengkapan' }</Label>
                          <SearchableSelect
                            options={ inclusionsLocations.map((location) => ({ value: location.id, label: location.name })) }
                            value={ formValues.inclusions_location_id }
                            onChange={ (value) => handleChange('inclusions_location_id', value) }
                            placeholder='Search Lokasi Kelengkapan'
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Kelengkapan di produk' }</Label>
                          <Input
                            type='text'
                            value={ formValues.inclusions_in_product }
                            onChange={ (event) => handleChange('inclusions_in_product', event.target.value) }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Kelengkapan di box' }</Label>
                          <Input
                            type='text'
                            value={ formValues.inclusions_in_box }
                            onChange={ (event) => handleChange('inclusions_in_box', event.target.value) }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Kelengkapan di brankas' }</Label>
                          <Input
                            type='text'
                            value={ formValues.inclusions_in_safe_deposit_box }
                            onChange={ (event) => handleChange('inclusions_in_safe_deposit_box', event.target.value) }
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup>
                      <Label>{ 'Internal Note Produk' }</Label>
                      <Input
                        type='textarea'
                        value={ formValues.internal_note }
                        onChange={ (event) => handleChange('internal_note', event.target.value) }
                      />
                    </FormGroup>

                    <Row>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Status' }</Label>
                          <Input
                            type='select'
                            value={ formValues.status }
                            onChange={ (event) => handleChange('status', event.target.value) }
                          >
                            <option value=''>{ 'Exist' }</option>
                            <option value='SOLD'>{ 'Sold' }</option>
                            <option value='RETURN'>{ 'Return' }</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={ 4 }>
                        <FormGroup>
                          <Label>{ 'Status Date' }</Label>
                          <Input
                            type='datetime-local'
                            value={ formValues.status_date }
                            onChange={ (event) => handleChange('status_date', event.target.value) }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <h5>{ 'Reference / Audit Info' }</h5>
                    <Table size='sm' responsive className='mb-0'>
                      <tbody>
                        <tr>
                          <td>{ 'Stock Opname' }</td>
                          <td>{ stockOpnameEntry?.stock_opname_title || '-' }</td>
                        </tr>
                        <tr>
                          <td>{ 'Supplier' }</td>
                          <td>{ stockOpnameEntry?.supplier || '-' }</td>
                        </tr>
                        <tr>
                          <td>{ 'Expected IQ' }</td>
                          <td>{ stockOpnameEntry?.expected_inventory_quantity || 0 }</td>
                        </tr>
                        <tr>
                          <td>{ 'Expected SQ' }</td>
                          <td>{ stockOpnameEntry?.expected_sellable_quantity || 0 }</td>
                        </tr>
                        <tr>
                          <td>{ 'Actual Qty' }</td>
                          <td>{ stockOpnameEntry?.actual_quantity ?? '-' }</td>
                        </tr>
                        <tr>
                          <td>{ 'Difference' }</td>
                          <td>{ stockOpnameEntry?.quantity_difference ?? '-' }</td>
                        </tr>
                        <tr>
                          <td>{ 'Correction' }</td>
                          <td>
                            {
                              stockOpnameEntry?.needs_correction
                                ? <Badge color='warning'>{ stockOpnameEntry?.correction_status || 'Diff' }</Badge>
                                : <Badge color='secondary'>{ 'No' }</Badge>
                            }
                          </td>
                        </tr>
                        <tr>
                          <td>{ 'Updated By' }</td>
                          <td>{ stockOpnameEntry?.updated_by_emails || '-' }</td>
                        </tr>
                        <tr>
                          <td>{ 'Last Updated At' }</td>
                          <td>{ stockOpnameEntry?.updated_at || '-' }</td>
                        </tr>
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Form>
            </>
          )
      }
    </>
  )
}

StockOpnameEntryEditPage.propTypes = propTypes

export default StockOpnameEntryEditPage
